<script lang="ts">
	import logo from "../../../core/assets/logo.svg";
	import type { Product } from "../../../core/schema/Product.js";
	import type { UploadedFile } from "../../../core/schema/UploadedFile.js";
	import type { Image } from "../../../core/schema/Image.js";
	import DynamicPicture from "./Picture/DynamicPicture.svelte";
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import { appName } from "../../../core/schema/names/appName.js";
	import translations from "./ProductGallery.translations.json";
	import DynamicVideo from "./video/DynamicVideo.svelte";
	import { isFileVideo } from "../../../core/utils/isFileVideo.js";
	import { getSharedTranslate } from "../utils/getSharedTranslate.js";

	export let product: Product;
	export let media: Image[];
	export let containerClass = "";
	export { containerClass as class };

	const bigMediaWidth = 600;
	const smallMediaWidth = 300;

	$: videos = media.filter((image) => isFileVideo(image));
	$: images = media.filter((image) => !isFileVideo(image));

	$: [primaryImage, ...otherImages] = images;

	let displayedMedia: UploadedFile | undefined;

	function displayImage(media: UploadedFile): void {
		displayedMedia = media;
	}

	function displayPrimaryImage(): void {
		displayedMedia = primaryImage;
	}

	const translate = getSharedTranslate(translations);
</script>

<div class="relative w-full md:min-w-[20.5rem] lg:min-w-[30.5rem] xl:min-w-[38.5rem] {containerClass}">
	{#if displayedMedia && isFileVideo(displayedMedia)}
		<DynamicVideo
			class="aspect-square w-full rounded-lg bg-white p-4"
			videoClass="w-full h-full rounded-lg aspect-square object-contain"
			video={displayedMedia}
			lightboxId="{product.name} - desktop"
			alt="{translate('photo')} – {product.name} (1/{media.length})"
			width={bigMediaWidth}
			height={bigMediaWidth}
		/>
	{:else if primaryImage}
		<DynamicPicture
			class="aspect-square w-full rounded-lg bg-white p-4"
			imgClass="w-full h-full rounded-lg aspect-square object-contain"
			image={displayedMedia ?? primaryImage}
			lightboxId="{product.name} - desktop"
			lightboxWidth={1000}
			width={bigMediaWidth}
			height={bigMediaWidth}
			bucket="storage"
			loading="eager"
			alt="{translate('photo')} – {product.name} (1/{media.length})"
			sourcesets={{
				">lg": [600, 0],
				">xl": [1400, 0],
			}}
		/>
	{:else}
		<div class="bg-secondaryBlack-40 flex aspect-square w-full items-center justify-center rounded-lg bg-white p-4">
			<StaticPicture
				alt="Logo {appName}"
				image={logo}
				class="w-28 opacity-50 lg:w-36 xl:w-40"
				loading="eager"
				width={160}
				height={160}
			/>
		</div>
	{/if}
	<div class="mt-2 grid-flow-row grid-cols-6 gap-2 {media.length < 6 ? 'flex' : 'grid'}">
		{#each videos as video, i}
			<DynamicVideo
				on:mouseover={() => {
					displayImage(video);
				}}
				on:mouseout={displayPrimaryImage}
				class="aspect-square  h-full max-h-[6rem] w-full max-w-[6rem] rounded-lg bg-white p-2 transition-transform duration-300 hover:scale-105"
				videoClass="w-full h-full rounded-md aspect-square object-cover"
				autoplay={false}
				{video}
				width={smallMediaWidth}
				height={smallMediaWidth}
				icon={true}
				lightboxId="{product.name} - desktop"
				alt="{translate('photo')} – {product.name} ({i + 2}/{media.length})"
			/>
		{/each}
		{#each otherImages as image, i}
			<DynamicPicture
				class="aspect-square h-full max-h-[6rem] w-full max-w-[6rem] rounded-lg bg-white p-2 transition-transform duration-300 hover:scale-105"
				imgClass="w-full h-full rounded-md object-cover"
				{image}
				lightboxId="{product.name} - desktop"
				lightboxWidth={1000}
				width={smallMediaWidth}
				height={smallMediaWidth}
				bucket="storage"
				loading="eager"
				alt="{translate('photo')} – {product.name} ({i + videos.length + 2}/{media.length})"
				on:mouseover={() => {
					displayImage(image);
				}}
				on:mouseout={displayPrimaryImage}
			/>
		{/each}
	</div>
</div>
